/**
 * Online quote acceptance (services/api/src/offers.ts, docs/decisions/0016):
 * - OfferDialog, for the installer: the quote frozen behind a link (title, number, validity,
 *   deposit, terms), the link to copy or email, and the quotes of this project sent before
 *   with their status and the client's signature once accepted;
 * - OfferView, the client's page (#/offer/<token>, no account): the quote, name, signature and
 *   "I accept", then the deposit with its SEPA QR code.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Project } from '@elec/domain';
import { LOCALES, localeTag, type Locale, type Translate } from '@elec/i18n';
import type { Platform } from '@elec/platform';
import { CloudClient, type Offer, type OfferDoc } from '@elec/sync';
import type * as Exporters from '@elec/export';
import { loadProfile } from './InvoiceDialog.tsx';
import { SignaturePad } from './Signoff.tsx';
import { Dialog } from './ui.tsx';

type X = typeof Exporters;

const money = (n: number, l: Locale) =>
  n.toLocaleString(localeTag(l), { style: 'currency', currency: 'EUR' });
const day = (iso: string, l: Locale) =>
  new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(localeTag(l), {
    dateStyle: 'long',
  });

/** Status line of a sent quote. */
function statusText(o: Offer, t: Translate, l: Locale) {
  if (o.status === 'accepted' && o.accepted)
    return t('offer.statusAccepted', { name: o.accepted.name, date: day(o.accepted.at, l) });
  if (o.status === 'withdrawn') return t('offer.statusWithdrawn');
  if (o.status === 'expired') return t('offer.statusExpired', { date: day(o.validUntil, l) });
  return t('offer.statusOpen', { date: day(o.validUntil, l) });
}

export function OfferDialog(props: {
  project: Project;
  quote: Exporters.Quote;
  t: Translate;
  locale: Locale;
  platform: Platform;
  client: CloudClient | null;
  load(): Promise<X>;
  notify(msg: string): void;
  onClose(): void;
}) {
  const { t, project, locale, quote } = props;
  const [x, setX] = useState<X | null>(null);
  const [title, setTitle] = useState(project.meta.title);
  const [number, setNumber] = useState('');
  const [validDays, setValidDays] = useState(30);
  const [depositPct, setDeposit] = useState(30);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState<{ url: string } | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seller = useMemo(() => loadProfile(props.platform, project).party, [project]);
  useEffect(() => void props.load().then(setX), []);
  const refresh = () =>
    props.client
      ?.offers(project.id)
      .then((r) => setOffers(r.offers))
      .catch(() => undefined);
  useEffect(() => void refresh(), [props.client]);
  const unpriced = x ? x.unpricedLines(quote).length : 0;
  const send = async () => {
    if (!x || !props.client) return;
    setBusy(true);
    setError(null);
    try {
      const doc = x.offerFromQuote(project, quote, seller, { title, number, depositPct, note, locale });
      const r = await props.client.sendOffer(project.id, doc as OfferDoc, validDays);
      setSent(r);
      void refresh();
    } catch (e) {
      setError(t('offer.failed', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  };
  const mailto = (url: string) =>
    `mailto:${encodeURIComponent(project.meta.ownerEmail ?? '')}?subject=${encodeURIComponent(
      t('offer.mailSubject', { title }),
    )}&body=${encodeURIComponent(t('offer.mailBody', { title, url, seller: seller.name || project.meta.company }))}`;
  const num = (v: string, d: number, max: number) => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? Math.min(max, n) : d;
  };
  return (
    <Dialog
      title={t('offer.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.close')}</button>
          {!sent && (
            <button
              className="primary"
              disabled={!props.client || !x || busy || unpriced > 0 || !title.trim()}
              onClick={() => void send()}
              data-testid="offer-create"
            >
              {t('offer.create')}
            </button>
          )}
        </>
      }
    >
      <p className="muted">{t('offer.intro')}</p>
      {!props.client && <p className="warn">{t('offer.signIn')}</p>}
      {unpriced > 0 && <p className="warn">{t('offer.unpriced', { n: unpriced })}</p>}
      {error && <p className="warn">{error}</p>}
      {sent ? (
        <div className="offer-link" data-testid="offer-link">
          <label htmlFor="offer-url">{t('offer.link')}</label>
          <input id="offer-url" readOnly value={sent.url} onFocus={(e) => e.currentTarget.select()} />
          <div className="offer-link-actions">
            <button
              onClick={() =>
                void navigator.clipboard?.writeText(sent.url).then(() => props.notify(t('offer.copied')))
              }
            >
              {t('offer.copy')}
            </button>
            <a className="button" href={mailto(sent.url)}>
              {t('offer.email')}
            </a>
          </div>
        </div>
      ) : (
        <div className="form-grid">
          <div className="field">
            <label htmlFor="of-title">{t('offer.docTitle')}</label>
            <input
              id="of-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="offer-title"
            />
          </div>
          <div className="field">
            <label htmlFor="of-number">{t('offer.number')}</label>
            <input id="of-number" value={number} onChange={(e) => setNumber(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="of-valid">{t('offer.valid')}</label>
            <input
              id="of-valid"
              inputMode="numeric"
              value={validDays}
              onChange={(e) => setValidDays(Math.max(1, Math.round(num(e.target.value, 30, 180))))}
            />
          </div>
          <div className="field">
            <label htmlFor="of-deposit">{t('offer.deposit')}</label>
            <input
              id="of-deposit"
              inputMode="decimal"
              value={depositPct}
              onChange={(e) => setDeposit(num(e.target.value, 0, 100))}
              data-testid="offer-deposit"
            />
          </div>
          <div className="field wide">
            <label htmlFor="of-note">{t('offer.note')}</label>
            <textarea id="of-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {depositPct > 0 && x && !x.validIban(seller.iban) && (
            <p className="muted small">{t('offer.noIban')}</p>
          )}
        </div>
      )}
      <h4>{t('offer.sent')}</h4>
      {offers.length === 0 ? (
        <p className="muted">{t('offer.none')}</p>
      ) : (
        <ul className="offer-list" data-testid="offer-list">
          {offers.map((o) => (
            <li key={o.token} data-status={o.status}>
              <div>
                <strong>{o.doc.title}</strong> · {money(o.doc.total, locale)}
                <br />
                <span className={o.status === 'accepted' ? 'ok' : 'muted'}>{statusText(o, t, locale)}</span>
              </div>
              {o.accepted?.signature && (
                <img
                  className="offer-signature"
                  alt={t('offer.signature', { name: o.accepted.name })}
                  src={`data:image/png;base64,${o.accepted.signature}`}
                />
              )}
              {o.status === 'open' && (
                <button
                  onClick={() =>
                    void props.client
                      ?.withdrawOffer(o.token)
                      .then(refresh)
                      .catch((e: Error) => setError(e.message))
                  }
                >
                  {t('offer.withdraw')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

export function OfferView(props: {
  token: string;
  baseUrl: string | undefined;
  t: Translate;
  locale: Locale;
  setLocale(l: Locale): void;
}) {
  const { t, locale } = props;
  const [offer, setOffer] = useState<Offer | 'loading' | 'error'>('loading');
  const [x, setX] = useState<X | null>(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [empty, setEmpty] = useState(true);
  const [clear, setClear] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thanks, setThanks] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const pad = useRef<(() => Promise<Uint8Array | null>) | null>(null);
  const api = useMemo(() => (props.baseUrl ? new CloudClient(props.baseUrl) : null), [props.baseUrl]);
  useEffect(() => {
    if (!api) return setOffer('error');
    api
      .offer(props.token)
      .then(setOffer)
      .catch(() => setOffer('error'));
    void import('@elec/export').then(setX);
  }, [api, props.token]);
  const doc = typeof offer === 'object' ? offer.doc : null;
  useEffect(() => {
    if (!x || !doc || typeof offer !== 'object' || offer.status !== 'accepted') return;
    const epc = x.depositEpc(doc);
    setQr(epc ? x.qrSvg(epc, 200) : null);
  }, [x, offer]);
  if (offer === 'loading')
    return (
      <main className="home-view offer-view">
        <p className="muted">{t('offerPage.loading')}</p>
      </main>
    );
  if (offer === 'error' || !doc)
    return (
      <main className="home-view offer-view">
        <p data-testid="offer-error">{t('offerPage.error')}</p>
      </main>
    );
  const accept = async () => {
    if (!api || !pad.current) return;
    setBusy(true);
    setError(null);
    try {
      const png = await pad.current();
      if (!png) return;
      let bin = '';
      for (const b of png) bin += String.fromCharCode(b);
      const r = await api.acceptOffer(props.token, { name: name.trim(), signature: btoa(bin), agree: true });
      setOffer(r);
      setThanks(true);
    } catch (e) {
      setError(t('offerPage.failed', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  };
  const lineTotal = (l: OfferDoc['lines'][number]) =>
    (l.unitPrice ?? 0) * l.quantity * (l.section === 'labour' ? 1 : 1 - doc.discountPct / 100);
  const deposit = Math.round(doc.total * doc.depositPct) / 100;
  const qty = (n: number) => n.toLocaleString(localeTag(locale), { maximumFractionDigits: 2 });
  return (
    <main className="home-view offer-view" data-testid="offer-view">
      <header>
        <h1>{doc.title}</h1>
        <p className="muted">
          {t('offerPage.from', { seller: doc.seller.name })}
          {doc.number && ` · ${doc.number}`}
        </p>
        <select
          value={locale}
          onChange={(e) => props.setLocale(e.target.value as Locale)}
          aria-label={t('menu.language')}
        >
          {Object.entries(LOCALES).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </header>
      <section className="offer-parties">
        <div>
          <strong>{doc.seller.name}</strong>
          <br />
          {doc.seller.address}
          {doc.seller.vatNumber && (
            <>
              <br />
              {doc.seller.vatNumber}
            </>
          )}
        </div>
        <div>
          <span className="muted">{t('offerPage.for')}</span>
          <br />
          <strong>{doc.client.name}</strong>
          <br />
          {doc.client.address}
          {doc.site && (
            <>
              <br />
              <span className="muted">{t('offerPage.site')} :</span> {doc.site}
            </>
          )}
        </div>
      </section>
      <p className="muted">
        {t('offerPage.dates', { date: day(doc.date, locale), until: day(offer.validUntil, locale) })}
      </p>
      <div className="quote-table offer-lines">
        <table>
          <thead>
            <tr>
              <th>{t('offerPage.colDesc')}</th>
              <th className="num">{t('offerPage.colQty')}</th>
              <th className="num">{t('offerPage.colPrice')}</th>
              <th className="num">{t('offerPage.colTotal')}</th>
            </tr>
          </thead>
          <tbody data-testid="offer-lines">
            {doc.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.description}</td>
                <td className="num">
                  {qty(l.quantity)} {l.unit}
                </td>
                <td className="num">{l.unitPrice === undefined ? '—' : money(l.unitPrice, locale)}</td>
                <td className="num">{money(lineTotal(l), locale)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>{t('offerPage.subtotal')}</td>
              <td className="num">{money(doc.subtotal, locale)}</td>
            </tr>
            <tr>
              <td colSpan={3}>{t('offerPage.vat', { pct: doc.vatPct })}</td>
              <td className="num">{money(doc.vat, locale)}</td>
            </tr>
            <tr className="offer-total">
              <td colSpan={3}>{t('offerPage.total')}</td>
              <td className="num" data-testid="offer-total">
                {money(doc.total, locale)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {doc.discountPct > 0 && (
        <p className="muted small">{t('offerPage.discount', { pct: doc.discountPct })}</p>
      )}
      {doc.note && <p className="offer-note">{doc.note}</p>}

      {offer.status === 'open' && (
        <section className="offer-accept" data-testid="offer-accept">
          <h2>{t('offerPage.acceptTitle')}</h2>
          {doc.depositPct > 0 && (
            <p>{t('offerPage.depositAsk', { pct: doc.depositPct, amount: money(deposit, locale) })}</p>
          )}
          <label htmlFor="of-name">{t('offerPage.name')}</label>
          <input
            id="of-name"
            value={name}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            data-testid="offer-name"
          />
          <span className="rm-label">{t('offerPage.sign')}</span>
          <SignaturePad t={t} pad={pad} clear={clear} onChange={setEmpty} />
          <button className="link" onClick={() => setClear((c) => c + 1)}>
            {t('signoff.clear')}
          </button>
          <label className="check">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              data-testid="offer-agree"
            />
            <span>{t('offerPage.agree')}</span>
          </label>
          {error && <p className="warn">{error}</p>}
          <button
            className="primary"
            disabled={busy || empty || !agree || name.trim().length < 2}
            onClick={() => void accept()}
            data-testid="offer-submit"
          >
            {busy ? t('offerPage.sending') : t('offerPage.accept')}
          </button>
          <p className="muted small">{t('offerPage.privacy')}</p>
        </section>
      )}
      {offer.status === 'accepted' && offer.accepted && (
        <section className="offer-done" data-testid="offer-done">
          <p className="ok">
            <strong>
              {thanks
                ? t('offerPage.thanks', { seller: doc.seller.name })
                : t('offerPage.accepted', {
                    name: offer.accepted.name,
                    date: day(offer.accepted.at, locale),
                  })}
            </strong>
          </p>
          {doc.depositPct > 0 && (
            <>
              <h2>{t('offerPage.pay', { amount: money(deposit, locale) })}</h2>
              {qr && (
                <div className="offer-qr" data-testid="offer-qr" dangerouslySetInnerHTML={{ __html: qr }} />
              )}
              {doc.seller.iban && (
                <p>
                  {t('offerPage.payHow', {
                    amount: money(deposit, locale),
                    iban: doc.seller.iban,
                    name: doc.seller.name,
                    ref: doc.paymentRef,
                  })}
                </p>
              )}
            </>
          )}
        </section>
      )}
      {offer.status === 'expired' && (
        <p className="warn" data-testid="offer-expired">
          {t('offerPage.expired', { date: day(offer.validUntil, locale) })}
        </p>
      )}
      <footer className="muted small">
        {t('offerPage.contact', { seller: doc.seller.name })}
        {[doc.seller.phone, doc.seller.email].filter(Boolean).map((c) => ` · ${c}`)}
      </footer>
    </main>
  );
}
